// ─────────────────────────────────────────────────────────────────────────────
// plans.config.ts — Single source of truth for plan gates.
// ⚠️  Keep frontend (src/config/plans.config.ts) copy in sync.
// To change a limit: edit PLAN_LIMITS below. No other file needs to change.
// ─────────────────────────────────────────────────────────────────────────────

export type Plan = "free" | "pro" | "business" | "enterprise";

export interface PlanLimits {
  // ── Numeric limits (-1 = unlimited) ──────────────────────────────────────
  maxDocuments: number; // per company
  maxTeamMembers: number; // total users in the company (team size limit)
  maxAiScansPerMonth: number; // document uploads with AI extraction
  maxAiChatsPerDay: number; // chat messages per day
  maxCompanies: number; // multi-tenant companies

  // ── Boolean feature flags ─────────────────────────────────────────────────
  csvImport: boolean;
  customReminderIntervals: boolean;
  customReminderTemplates: boolean;
  reminderWebhooks: boolean;
  externalOwnerEmails: boolean; // owners who are not registered users
  customCategories: boolean;
  auditTrail: boolean;
  snoozeReminders: boolean;
  multiCompany: boolean;
  apiAccess: boolean;
  whiteLabel: boolean;
  ssoSaml: boolean;
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  // ── FREE ──────────────────────────────────────────────────────────────────
  free: {
    maxDocuments: 15,
    maxTeamMembers: 1,
    maxAiScansPerMonth: 5,
    maxAiChatsPerDay: 10,
    maxCompanies: 1,
    csvImport: false,
    customReminderIntervals: false,
    customReminderTemplates: false,
    reminderWebhooks: false,
    externalOwnerEmails: false,
    customCategories: false,
    auditTrail: false,
    snoozeReminders: false,
    multiCompany: false,
    apiAccess: false,
    whiteLabel: false,
    ssoSaml: false,
  },

  // ── PRO ($19/mo) ──────────────────────────────────────────────────────────
  pro: {
    maxDocuments: -1, // unlimited
    maxTeamMembers: 5,
    maxAiScansPerMonth: 100,
    maxAiChatsPerDay: -1,
    maxCompanies: 1,
    csvImport: true,
    customReminderIntervals: true,
    customReminderTemplates: false, // Business+ only — synced with backend
    reminderWebhooks: false,        // Business+ only — synced with backend
    externalOwnerEmails: true,
    customCategories: true,
    auditTrail: true,
    snoozeReminders: true,
    multiCompany: false,
    apiAccess: false,
    whiteLabel: false,
    ssoSaml: false,
  },

  // ── BUSINESS ($49/mo) ─────────────────────────────────────────────────────
  business: {
    maxDocuments: -1,
    maxTeamMembers: 25,
    maxAiScansPerMonth: -1,
    maxAiChatsPerDay: -1,
    maxCompanies: 3,
    csvImport: true,
    customReminderIntervals: true,
    customReminderTemplates: true,
    reminderWebhooks: true,
    externalOwnerEmails: true,
    customCategories: true,
    auditTrail: true,
    snoozeReminders: true,
    multiCompany: true,
    apiAccess: false,
    whiteLabel: false,
    ssoSaml: false,
  },

  // ── ENTERPRISE (Custom) ───────────────────────────────────────────────────
  enterprise: {
    maxDocuments: -1,
    maxTeamMembers: -1,
    maxAiScansPerMonth: -1,
    maxAiChatsPerDay: -1,
    maxCompanies: -1,
    csvImport: true,
    customReminderIntervals: true,
    customReminderTemplates: true,
    reminderWebhooks: true,
    externalOwnerEmails: true,
    customCategories: true,
    auditTrail: true,
    snoozeReminders: true,
    multiCompany: true,
    apiAccess: true,
    whiteLabel: true,
    ssoSaml: true,
  },
};

// ── Metadata ──────────────────────────────────────────────────────────────────
export const PLAN_LABELS: Record<Plan, string> = {
  free: "Free",
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
};

export const PLAN_UPGRADE_ORDER: Plan[] = [
  "free",
  "pro",
  "business",
  "enterprise",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
export function getPlanLimits(plan: Plan): PlanLimits {
  return PLAN_LIMITS[plan] ?? PLAN_LIMITS["free"];
}

export function isUnlimited(value: number): boolean {
  return value === -1;
}

/** Returns the lowest plan that unlocks a given feature */
export function getRequiredPlan(feature: keyof PlanLimits): Plan {
  for (const plan of PLAN_UPGRADE_ORDER) {
    const val = PLAN_LIMITS[plan][feature];
    if (val === true || val === -1 || (typeof val === "number" && val > 0)) {
      return plan;
    }
  }
  return "enterprise";
}

// ── Workspace plan helpers ─────────────────────────────────────────────────────
/**
 * Returns the effective plan for the ACTIVE WORKSPACE.
 *
 * In the multi-workspace model the plan lives on the workspace (companies.plan),
 * not the user. The JWT already carries the workspace plan, so the user object's
 * `plan` field is always "what this workspace allows right now".
 *
 * Calling convention (from useAuth):
 *   const { plan } = workspacePlanContext(user);
 *   canUse("auditTrail", plan);  // uses workspace plan, not user's personal plan
 */
export function resolveWorkspacePlan(userPlan: string | undefined): Plan {
  const p = (userPlan ?? "free") as Plan;
  return PLAN_LIMITS[p] ? p : "free";
}

/**
 * Given a workspace plan, returns a human-readable context string for the UI.
 * e.g. "You are using Business features in this workspace."
 */
export function workspacePlanBadge(plan: Plan, isPersonal: boolean): string {
  if (isPersonal) return `${PLAN_LABELS[plan]} (your plan)`;
  return `${PLAN_LABELS[plan]} (workspace plan)`;
}
